import { FC, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as IconArrowDown } from '../../../../assets/images/arrow.svg';
import { ReactComponent as IconDownload } from '../../../../assets/images/download.svg';
import {
    bookingYear,
    factsheetLinkPDF,
    factsheetLinkXLSX,
    getFactSheet,
    setFactSheetPDF,
    setFactSheetXLSX,
} from '../../../../store/location';
import '../checker/index.scss';
import { loadersByName } from '../../../../store/ui';
import pushToDataLayer from '../../../../utils/pushToDataLayer';
import { openInNewTab } from '../../../../utils/urls';
import Button, { ButtonKind } from '../button/Button';
import Loading from '../Loading';

import './index.scss';

const DownloadFactSheet: FC<{ isFactSheetLabel: boolean; isHideText?: boolean; urns: string[] }> = ({
    urns,
    isHideText,
    isFactSheetLabel,
}) => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const loading = useSelector(loadersByName('factSheet'));
    const linkPDF = useSelector(factsheetLinkPDF);
    const linkXLSX = useSelector(factsheetLinkXLSX);
    const bookingyear = useSelector(bookingYear);
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        dispatch(setFactSheetPDF(''));
        dispatch(setFactSheetXLSX(''));
        const handleClickOutside = (event: Event) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            if (ref.current && !(ref.current as never).contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('click', handleClickOutside, true);
        return () => {
            dispatch(setFactSheetPDF(''));
            dispatch(setFactSheetXLSX(''));
            document.removeEventListener('click', handleClickOutside, true);
        };
    }, [dispatch]);

    const activatePopup = () => setIsOpen(true);

    const downloadFactsheet = (format: string) => {
        pushToDataLayer('product_finder_factsheet_download_button');
        const fileLink = format === 'pdf' ? linkPDF : linkXLSX;
        if (fileLink) {
            openInNewTab(fileLink);
        } else {
            dispatch(getFactSheet({ bookingyear: bookingyear, producturns: urns, format: format }));
        }
        setIsOpen(false);
    };

    return (
        <div className={'download-button-menu'} ref={ref}>
            {isOpen && (
                <div className={`download-menu ${isFactSheetLabel ? '' : 'full-width'}`}>
                    <div
                        className={'download-menu__item'}
                        onClick={() => {
                            downloadFactsheet('pdf');
                        }}
                    >
                        PDF
                    </div>
                    <div
                        className={'download-menu__item'}
                        onClick={() => {
                            downloadFactsheet('xlslist');
                        }}
                    >
                        Excel
                    </div>
                </div>
            )}
            <Button
                kind={ButtonKind.LINK}
                // kind={isFactSheetLabel ? ButtonKind.LINK : ButtonKind.INVERT_GRAY}
                onClick={isFactSheetLabel ? () => downloadFactsheet('pdf') : activatePopup}
                className={`download-link ${isOpen ? 'opened' : ''}`}
                disabled={loading}
            >
                {loading ? (
                    <>
                        <Loading style={{ margin: 0, width: 40 }} relative />
                        {intl.formatMessage({ id: 'general.factSheetLoading', defaultMessage: 'Bitte warten' })}
                    </>
                ) : (
                    <>
                        <IconDownload />
                        {isHideText
                            ? ''
                            : isFactSheetLabel
                            ? intl.formatMessage({ id: 'general.factSheet', defaultMessage: 'Download Factsheet (PDF)' })
                            : intl.formatMessage({ id: 'general.downloadPDF', defaultMessage: 'Download' })}
                        {!isFactSheetLabel && <IconArrowDown />}
                    </>
                )}
            </Button>
        </div>
    );
};

export default DownloadFactSheet;
